/*
 * Product reviews without the reviews.io widget.
 *
 * rug-pdp.js paints the score, star breakdown and the rating under the title. This file adds:
 *  - the product's rating as structured data for search engines (the reviews.io widget used to add it),
 *  - the latest written reviews, rendered when the section nears the screen, with a "Show more" button,
 *  - hiding the whole section when the product has no reviews.
 * One small request to reviews.io's public API after the page has loaded; no third-party scripts.
 */
(() => {
  const root = document.querySelector(".rug-reviews");
  if (!root || root.dataset.reviewsLite) return;
  root.dataset.reviewsLite = "1";

  const store = root.dataset.reviewsStore;
  const sku = root.dataset.productId;
  const list = root.querySelector("[data-rug-reviews-list]");
  const more = root.querySelector("[data-rug-reviews-more]");
  if (!store || !sku || !list) return;

  const PER_PAGE = 10;
  const FIRST = 3;
  const STEP = 5;
  const verifiedLabel = root.dataset.verifiedLabel || "";
  const starsLabel = root.dataset.starsLabel || "{value}/5";

  let config = {};
  try {
    config = JSON.parse(document.getElementById("rr-pdp-config")?.textContent || "{}");
  } catch {
    config = {};
  }
  const dateLocale = config.dateLocale || document.documentElement.lang || "en-GB";

  let page = 0;
  let total = 0;
  let exhausted = false;
  let shown = 0;
  const pool = [];

  async function loadPage() {
    if (exhausted) return null;
    page += 1;
    const url =
      "https://api.reviews.io/reviews?store=" +
      encodeURIComponent(store) +
      "&sku=" +
      encodeURIComponent(sku) +
      "&type=product_review&per_page=" +
      PER_PAGE +
      "&page=" +
      page;
    const res = await fetch(url);
    if (!res.ok) throw new Error("reviews " + res.status);
    const data = await res.json();
    const reviews = Array.isArray(data.reviews) ? data.reviews : [];
    total = Number(data.review_count || 0);
    pool.push(...reviews.filter((r) => String(r.comments || "").trim()));
    if (!reviews.length || page * PER_PAGE >= total) exhausted = true;
    return data;
  }

  function hideSection() {
    const section = root.closest(".shopify-section");
    (section || root).hidden = true;
  }

  function addStructuredData(data) {
    const count = Number(data.review_count || 0);
    const average = Number(data.average_rating || 0);
    if (!count || !average) return;
    let product = null;
    document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
      if (product) return;
      try {
        const json = JSON.parse(el.textContent);
        const nodes = Array.isArray(json["@graph"]) ? json["@graph"] : [json];
        product = nodes.find((n) => n && n["@type"] === "Product") || null;
      } catch {
        /* ignore other scripts' JSON */
      }
    });
    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product?.name || document.title,
      url: product?.url || location.origin + location.pathname,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: String(Math.round(average * 100) / 100),
        reviewCount: String(count),
        bestRating: "5",
        worstRating: "1",
      },
    };
    if (product?.["@id"]) ld["@id"] = product["@id"];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.rugReviewsRating = "";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
  }

  function shortName(name) {
    const clean = String(name || "").trim();
    if (!clean || /^anonymous$/i.test(clean)) return "";
    const parts = clean.split(/\s+/);
    const cap = (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    const first = cap(parts[0]);
    return parts.length > 1 ? `${first} ${parts[parts.length - 1].charAt(0).toUpperCase()}.` : first;
  }

  function shortPlace(location) {
    return String(location || "").split(",")[0].trim();
  }

  function monthYear(value) {
    const date = new Date(String(value || "").replace(" ", "T"));
    if (Number.isNaN(date.getTime())) return "";
    try {
      return new Intl.DateTimeFormat(dateLocale, { month: "long", year: "numeric" }).format(date);
    } catch {
      return date.getFullYear();
    }
  }

  function card(review) {
    const rating = Math.max(0, Math.min(5, Math.round(Number(review.rating) || 0)));
    const article = document.createElement("article");
    article.className = "rug-review";

    const stars = document.createElement("div");
    stars.className = "rug-review__stars";
    stars.setAttribute("role", "img");
    stars.setAttribute("aria-label", starsLabel.replace("{value}", rating));
    stars.innerHTML = '<span aria-hidden="true">' + "★".repeat(rating) + "</span>" + '<span aria-hidden="true" class="rug-review__stars-off">' + "★".repeat(5 - rating) + "</span>";
    article.appendChild(stars);

    const text = document.createElement("p");
    text.className = "rug-review__text";
    text.textContent = String(review.comments || "").trim();
    article.appendChild(text);

    const meta = [shortName(review.author?.name), shortPlace(review.author?.location), monthYear(review.date_created)].filter(Boolean);
    const line = document.createElement("p");
    line.className = "rug-review__meta";
    line.textContent = meta.join(" · ");
    if (Number(review.verified) === 1 && verifiedLabel) {
      const badge = document.createElement("span");
      badge.className = "rug-review__verified";
      badge.textContent = verifiedLabel;
      if (meta.length) line.append(" · ");
      line.appendChild(badge);
    }
    article.appendChild(line);
    return article;
  }

  function render(upTo) {
    const end = Math.min(upTo, pool.length);
    for (; shown < end; shown += 1) list.appendChild(card(pool[shown]));
    if (more) more.hidden = shown >= pool.length && exhausted;
  }

  const first = new Promise((resolve) => {
    const start = () => loadPage().then(resolve, () => resolve(null));
    const idle = () => ("requestIdleCallback" in window ? requestIdleCallback(start, { timeout: 2500 }) : setTimeout(start, 600));
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });
  });

  first.then((data) => {
    if (!data) return;
    if (!Number(data.review_count || 0)) {
      hideSection();
      return;
    }
    addStructuredData(data);
  });

  const show = () =>
    first.then((data) => {
      if (!data || !total) return;
      render(FIRST);
    });

  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        show();
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(root);
  } else {
    show();
  }

  more?.addEventListener("click", async () => {
    more.disabled = true;
    try {
      while (pool.length < shown + STEP && !exhausted) await loadPage();
    } catch {
      exhausted = true;
    }
    more.disabled = false;
    render(shown + STEP);
  });
})();
